/**
 * Per-order velocity-spike mute persistence.
 *
 * The velocity bell on an Open Order card mutes that order's spike alert. The
 * mute is trader intent about a specific resting order, so it must survive a
 * reload/remount while the order is still open — otherwise every refresh
 * silently un-mutes it (issue #140).
 *
 * Browser-local and per-user (mirrors savedGames): one key per signed-in user
 * so a logout/login on a shared browser can't inherit the previous trader's
 * mutes, and channel-namespaced via storageKey so preview channels don't bleed
 * into stable. Keys are order ids (`<kalshiOrderId>` or `poly-<id>`), the same
 * keys the tracker and card rows use. Nothing but the id set is stored.
 */

import { storageKey } from '@/lib/infra/storage';

const KEY_PREFIX = 'galactus:velocityMutes:';

function keyFor(userId: string): string {
  return storageKey(`${KEY_PREFIX}${userId}`);
}

/** The persisted muted-order ids for a user. Empty set when signed out, none
 *  stored, or storage is unreadable/corrupt. */
export function readVelocityMutes(userId: string | null | undefined): Set<string> {
  if (!userId) return new Set();
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

/** Persist the muted-order ids for a user. No-op when signed out; best-effort
 *  (a full/unavailable localStorage is swallowed, same as savedGames). */
export function writeVelocityMutes(userId: string | null | undefined, keys: Set<string>): void {
  if (!userId) return;
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify([...keys]));
  } catch {
    // localStorage full or unavailable — the mute still works in-session.
  }
}
